import {
    type ReactNode
} from "react";
import {
    StyleSheet,
    View
} from "react-native";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    NCoreUIKitTheme,
    Sparkline,
    StatTile,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    DocsTable,
    Callout,
    Article,
    Preview,
    Section
} from "../../components/content";

const NAME = "Sparkline";

const USAGE_CODE = `<Sparkline
    summary="Aktif kullanıcı, son 12 hafta: 6.120 kişiden 8.412 kişiye."
    values={[6120, 6380, 6290, 6710, 6950, 6820, 7240, 7510, 7380, 7860, 8130, 8412]}
    size="medium"
/>`;

const VARIANT_CODE = `<Sparkline
    variant="area"
    values={revenue}
    size="medium"
/>

<Sparkline
    variant="bars"
    values={orders}
    size="medium"
/>`;

const INTERACTIVE_CODE = `<Sparkline
    valueFormatter={(value) => \`₺\${value} B\`}
    summary="Aylık yinelenen gelir, son 12 ay."
    isInteractive={true}
    values={revenue}
    variant="area"
    size="medium"
/>`;

const TABLE_CODE = `<Table.Cell>
    <Sparkline
        summary="Son 12 hafta: 6.120 kişiden 8.412 kişiye."
        values={weekly}
    />
</Table.Cell>`;

const WEEKLY = [
    6120,
    6380,
    6290,
    6710,
    6950,
    6820,
    7240,
    7510,
    7380,
    7860,
    8130,
    8412
];

const REVENUE = [
    418,
    436,
    452,
    470,
    481,
    503,
    522,
    538,
    551,
    574,
    590,
    612
];

const ORDERS = [
    142,
    158,
    151,
    167,
    189,
    121,
    108,
    149,
    162,
    171,
    158,
    194,
    132,
    186
];

const WIN_LOSS = [
    1,
    1,
    -1,
    1,
    1,
    1,
    -1,
    1,
    1,
    -1,
    1,
    1,
    -1,
    1,
    -1,
    1,
    -1,
    1,
    1,
    1
];

const REGIONS = [
    {
        nameKey: "sparklinePage.regionMarmara",
        key: "marmara",
        values: [
            1820,
            1910,
            1880,
            2040,
            2110,
            2090,
            2230
        ]
    },
    {
        nameKey: "sparklinePage.regionAegean",
        key: "aegean",
        values: [
            940,
            910,
            960,
            930,
            900,
            880,
            860
        ]
    },
    {
        nameKey: "sparklinePage.regionAnatolia",
        key: "anatolia",
        values: [
            610,
            640,
            700,
            690,
            760,
            810,
            870
        ]
    }
] as const;

const SPARK_STEPS = [
    4,
    5
] as const;

const stylesheet = StyleSheet.create({
    row: {
        justifyContent: "space-between",
        flexDirection: "row",
        alignItems: "center"
    },
    block: {
        alignSelf: "stretch"
    }
});

const Figure = ({
    children,
    caption,
    label,
    value
}: {
    children: ReactNode;
    caption?: string;
    label: string;
    value: string;
}) => {
    const {
        spaces
    } = NCoreUIKitTheme.useContext();

    return <View
        style={[
            stylesheet.block,
            {
                gap: spaces.spacingXs
            }
        ]}
    >
        <View
            style={stylesheet.row}
        >
            <Text
                variant="labelMediumSize"
                color="mid"
            >
                {label}
            </Text>
            <Text
                variant="titleMediumSize"
                color="high"
            >
                {value}
            </Text>
        </View>
        {children}
        {
            caption ?
                <Text
                    variant="bodySmallSize"
                    color="low"
                >
                    {caption}
                </Text>
            :
                null
        }
    </View>;
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("section.variants"),
        id: "variants"
    },
    {
        title: t("sparklinePage.lastPointTitle"),
        id: "last-point"
    },
    {
        title: t("sparklinePage.interactiveTitle"),
        id: "interactive"
    },
    {
        title: t("sparklinePage.contextTitle"),
        id: "context"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const SparklinePage = () => {
    const {
        language,
        t
    } = useDocsI18n();

    const formatNumber = (value: number) => value.toLocaleString(language);

    const formatMoney = (value: number) => t("sparklinePage.moneyValue", [
        formatNumber(value)
    ]);

    const wins = WIN_LOSS.filter((value) => value > 0).length;

    return <Article>
        <ComponentHeader
            lead={t("sparklinePage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("sparklinePage.overviewText")}
            </Paragraph>
            <Paragraph>
                {t("sparklinePage.overviewNote")}
            </Paragraph>
            <Preview
                caption={t("sparklinePage.overviewCaption")}
                isStacked={true}
            >
                <Figure
                    caption={t("sparklinePage.lineCaption")}
                    label={t("sparklinePage.lineLabel")}
                    value={formatNumber(8412)}
                >
                    <Sparkline
                        summary={t("sparklinePage.lineSummary", [
                            formatNumber(6120),
                            formatNumber(8412)
                        ])}
                        values={WEEKLY}
                        size="medium"
                    />
                </Figure>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("section.variants")}
            id="variants"
        >
            <Paragraph>
                {t("sparklinePage.variantsText")}
            </Paragraph>
            <Preview
                caption={t("sparklinePage.variantsCaption")}
                isStacked={true}
            >
                <Figure
                    caption={t("sparklinePage.lineCaption")}
                    label={t("sparklinePage.lineLabel")}
                    value={formatNumber(8412)}
                >
                    <Sparkline
                        summary={t("sparklinePage.lineSummary", [
                            formatNumber(6120),
                            formatNumber(8412)
                        ])}
                        values={WEEKLY}
                        size="medium"
                    />
                </Figure>
                <Figure
                    caption={t("sparklinePage.areaCaption")}
                    label={t("sparklinePage.areaLabel")}
                    value={formatMoney(612)}
                >
                    <Sparkline
                        summary={t("sparklinePage.areaSummary", [
                            formatMoney(418),
                            formatMoney(612)
                        ])}
                        values={REVENUE}
                        variant="area"
                        size="medium"
                    />
                </Figure>
                <Figure
                    caption={t("sparklinePage.barsCaption")}
                    label={t("sparklinePage.barsLabel")}
                    value={formatNumber(186)}
                >
                    <Sparkline
                        summary={t("sparklinePage.barsSummary", [
                            formatNumber(Math.max(...ORDERS)),
                            formatNumber(186)
                        ])}
                        values={ORDERS}
                        variant="bars"
                        size="medium"
                    />
                </Figure>
                <Figure
                    caption={t("sparklinePage.winLossCaption")}
                    label={t("sparklinePage.winLossLabel")}
                    value={`${wins} / ${WIN_LOSS.length}`}
                >
                    <Sparkline
                        summary={t("sparklinePage.winLossSummary", [
                            wins,
                            WIN_LOSS.length - wins
                        ])}
                        isShowLastPoint={false}
                        values={WIN_LOSS}
                        variant="bars"
                        size="medium"
                    />
                </Figure>
            </Preview>
            <CodeBlock
                code={VARIANT_CODE}
                language="tsx"
            />
            <Callout>
                {t("sparklinePage.variantsNote")}
            </Callout>
        </Section>
        <Section
            title={t("sparklinePage.lastPointTitle")}
            id="last-point"
        >
            <Paragraph>
                {t("sparklinePage.lastPointText")}
            </Paragraph>
            <Preview
                caption={t("sparklinePage.lastPointCaption")}
                isStacked={true}
            >
                <Figure
                    label={t("sparklinePage.lastPointOn")}
                    value={formatNumber(8412)}
                >
                    <Sparkline
                        values={WEEKLY}
                        size="medium"
                    />
                </Figure>
                <Figure
                    label={t("sparklinePage.lastPointOff")}
                    value={formatNumber(8412)}
                >
                    <Sparkline
                        isShowLastPoint={false}
                        values={WEEKLY}
                        size="medium"
                    />
                </Figure>
                {
                    SPARK_STEPS.map((step) => <Figure
                        label={`sequentialStep={${step}}`}
                        value={formatNumber(8412)}
                        key={step}
                    >
                        <Sparkline
                            sequentialStep={step}
                            values={WEEKLY}
                            size="medium"
                        />
                    </Figure>)
                }
            </Preview>
        </Section>
        <Section
            title={t("sparklinePage.interactiveTitle")}
            id="interactive"
        >
            <Paragraph>
                {t("sparklinePage.interactiveText")}
            </Paragraph>
            <Preview
                caption={t("sparklinePage.interactiveCaption")}
                isStacked={true}
            >
                <Figure
                    label={t("sparklinePage.areaLabel")}
                    value={formatMoney(612)}
                >
                    <Sparkline
                        summary={t("sparklinePage.areaSummary", [
                            formatMoney(418),
                            formatMoney(612)
                        ])}
                        valueFormatter={formatMoney}
                        isInteractive={true}
                        values={REVENUE}
                        variant="area"
                        size="medium"
                    />
                </Figure>
                <Figure
                    label={t("sparklinePage.barsLabel")}
                    value={formatNumber(186)}
                >
                    <Sparkline
                        summary={t("sparklinePage.barsSummary", [
                            formatNumber(Math.max(...ORDERS)),
                            formatNumber(186)
                        ])}
                        valueFormatter={formatNumber}
                        isInteractive={true}
                        values={ORDERS}
                        variant="bars"
                        size="medium"
                    />
                </Figure>
            </Preview>
            <CodeBlock
                code={INTERACTIVE_CODE}
                language="tsx"
            />
            <Callout>
                {t("sparklinePage.interactiveNote")}
            </Callout>
        </Section>
        <Section
            title={t("sparklinePage.contextTitle")}
            id="context"
        >
            <Paragraph>
                {t("sparklinePage.contextText")}
            </Paragraph>
            <Preview
                caption={t("sparklinePage.statTileCaption")}
                isStacked={true}
            >
                <StatTile
                    isTrendFlush={true}
                    trend={WEEKLY}
                >
                    <StatTile.Title>
                        {t("sparklinePage.lineLabel")}
                    </StatTile.Title>
                    <StatTile.Value>
                        {formatNumber(8412)}
                    </StatTile.Value>
                    <StatTile.Delta
                        description={t("sparklinePage.vsLastMonth")}
                        value={0.035}
                    />
                </StatTile>
            </Preview>
            <Paragraph>
                {t("sparklinePage.tableText")}
            </Paragraph>
            <Preview
                caption={t("sparklinePage.tableCaption")}
                isStacked={true}
            >
                <DocsTable
                    rows={REGIONS.map((region) => [
                        t(region.nameKey),
                        formatNumber(region.values[region.values.length - 1] ?? 0),
                        <Sparkline
                            summary={t("sparklinePage.regionSummary", [
                                t(region.nameKey),
                                formatNumber(region.values[0] ?? 0),
                                formatNumber(region.values[region.values.length - 1] ?? 0)
                            ])}
                            values={[
                                ...region.values
                            ]}
                            key={region.key}
                        />
                    ])}
                    headers={[
                        t("sparklinePage.tableRegion"),
                        t("sparklinePage.tableLast"),
                        t("sparklinePage.tableTrend")
                    ]}
                />
            </Preview>
            <CodeBlock
                code={TABLE_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("sparklinePage.a11ySummary"),
                    t("sparklinePage.a11yDecorative"),
                    t("sparklinePage.a11yKeyboard"),
                    t("sparklinePage.a11yTouch"),
                    t("sparklinePage.a11yMotion")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("sparklinePage.propValues"),
                        type: "Array<number>",
                        name: "values"
                    },
                    {
                        description: t("sparklinePage.propVariant"),
                        type: "\"line\" | \"area\" | \"bars\"",
                        defaultValue: "\"line\"",
                        name: "variant"
                    },
                    {
                        description: t("sparklinePage.propSize"),
                        type: "\"small\" | \"medium\"",
                        defaultValue: "\"small\"",
                        name: "size"
                    },
                    {
                        description: t("sparklinePage.propIsShowLastPoint"),
                        name: "isShowLastPoint",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("sparklinePage.propIsInteractive"),
                        defaultValue: "false",
                        name: "isInteractive",
                        type: "boolean"
                    },
                    {
                        description: t("sparklinePage.propValueFormatter"),
                        type: "(value: number) => string",
                        name: "valueFormatter"
                    },
                    {
                        description: t("sparklinePage.propSequentialStep"),
                        name: "sequentialStep",
                        type: "4 | 5"
                    },
                    {
                        description: t("sparklinePage.propIsAnimatedOnMount"),
                        name: "isAnimatedOnMount",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("sparklinePage.propSummary"),
                        name: "summary",
                        type: "string"
                    },
                    {
                        description: t("sparklinePage.propIsDecorative"),
                        defaultValue: "false",
                        name: "isDecorative",
                        type: "boolean"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("sparklinePage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("sparklinePage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("sparklinePage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default SparklinePage;
